import React, { useEffect, useState } from 'react';
import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { END_AGENTIC_SESSION, GET_AGENT_STATUS, POST_USER_MESSAGE, START_AGENTIC_SESSION } from '@components/Copilot/gql';
import {
  AgenticSessionInput,
  AgenticWorkflowMode,
  AgentResponse,
  ChatbotEndSessionRequest,
  ChatbotErrorType,
  ChatbotStatus,
  ChatbotWfStatus,
  ChatMessage,
  ChatRunInfo,
  CopilotChatMessageType,
  CopilotGetStatusChatMessageType,
  EndChatbotSessionData,
  GenerateChatbotSummaryData,
  GenerateChatbotSummaryRequest,
  GetChatbotMessageResponse,
  InitChatbotSessionRequest,
  InitChatbotSessionResponse,
  PublishChatbotMessageData,
  PublishChatbotMessageRequest,
  TCopilotError,
  TSessionProps,
} from '@components/Copilot/types';
import { QueryKey } from '@fabric-ui/utils';
import { Query, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { GraphQLError } from 'graphql';
import { gql } from 'graphql-request';

// New hooks
export const useStartAgenticSession = (
  options?: Omit<
    UseMutationOptions<{ startAgenticSession: { sessionId: string } }, GraphQLError, { agenticSessionInput: AgenticSessionInput }>,
    'variablesAdapter'
  >
) =>
  useGqlMutation<{ startAgenticSession: { sessionId: string } }, { agenticSessionInput: AgenticSessionInput }>({
    mutationKey: [QueryKey.Copilot, 'startAgenticSession'],
    mutation: START_AGENTIC_SESSION,
    ...options,
  });

export const usePostUserMessage = (
  options?: Omit<UseMutationOptions<boolean, GraphQLError, { sessionId: string; message: string }>, 'variablesAdapter'>
) =>
  useGqlMutation<boolean, { sessionId: string; message: string }>({
    mutationKey: [QueryKey.Copilot, 'postUserMessage'],
    mutation: POST_USER_MESSAGE,
    ...options,
  });

export const useEndAgenticSession = (
  options?: Omit<UseMutationOptions<boolean, GraphQLError, { sessionId: string }>, 'variablesAdapter'>
) =>
  useGqlMutation<boolean, { sessionId: string }>({
    mutationKey: [QueryKey.Copilot, 'endAgenticSession'],
    mutation: END_AGENTIC_SESSION,
    ...options,
  });

export const useGetAgentStatus = ({
  sessionId,
  enabled = false,
  onSuccess,
  onError,
}: {
  sessionId: string;
  enabled?: boolean;
  onSuccess?: (val: AgentResponse['getAgentStatus']) => void;
  onError?: (error: GraphQLError) => void;
}) =>
  useGqlQuery<AgentResponse, AgentResponse['getAgentStatus']>({
    queryKey: [QueryKey.Copilot, 'getAgentStatus', sessionId],
    query: GET_AGENT_STATUS,
    variables: { sessionId },
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    onSuccess,
    onError,
    refetchInterval: (query: Query<AgentResponse, GraphQLError>) => {
      const status = query.state.data?.getAgentStatus?.status;
      if ([ChatbotStatus.LOADING_DATA, ChatbotStatus.TOOL_CALLING, ChatbotStatus.THINKING, ChatbotStatus.UNSPECIFIED].includes(status!)) {
        return 3000;
      }
      return false;
    },
  });

export const useAgenticConversation = ({
  isCopilotOpen = false,
  sessionId,
  addMessage,
  setErrorMessage,
  setSessionId,
  lastSessionProps,
}: {
  isCopilotOpen: boolean;
  sessionId: string;
  addMessage: (message: ChatMessage) => void;
  setErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
  setSessionId: (sessionId: string) => void;
  lastSessionProps: TSessionProps;
}) => {
  const queryClient = useQueryClient();
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const { mutate: startSession, isPending: isStartingSession } = useStartAgenticSession({
    onSuccess: ({ sessionId }: any) => {
      setSessionId(sessionId);
      setIsPollingEnabled(true);
    },
    onError: () => {
      setErrorMessage({
        message: 'Something went wrong while starting the session. Please try again.',
        type: ChatbotErrorType.INTERNAL,
      });
      setIsPollingEnabled(false);
    },
  });

  useEffect(() => {
    if (isCopilotOpen && !sessionId && lastSessionProps?.agentId) {
      startSession({
        agenticSessionInput: {
          agentId: lastSessionProps.agentId,
          workflowMode: AgenticWorkflowMode.AGENTIC_WORKFLOW_MODE_INTERACTIVE,
          additionalParams: lastSessionProps.additionalParams,
        },
      });
    }
    if (!isCopilotOpen) {
      setIsPollingEnabled(false);
    }
  }, [isCopilotOpen, sessionId, lastSessionProps, startSession]);

  const onError = () => {
    setErrorMessage({
      message: 'Something went wrong. Please try again.',
      type: ChatbotErrorType.INTERNAL,
    });
    setIsPollingEnabled(false);
  };

  const { mutate: postMessage, isPending: isPosting } = usePostUserMessage({
    onSuccess: () => {
      setIsPollingEnabled(true);
      queryClient.invalidateQueries({ queryKey: [QueryKey.Copilot, 'getAgentStatus', sessionId] });
    },
    onError,
  });

  useGetAgentStatus({
    sessionId,
    enabled: isPollingEnabled && isCopilotOpen && !!sessionId,
    onSuccess: response => {
      // eslint-disable-next-line no-underscore-dangle
      if (response.result?.__typename === 'AgentSuccess') {
        const value = response.result.structuredOutput?.value;
        const messageContent = value?.agent_response || response.result.content;
        if (messageContent) {
          addMessage({
            content: messageContent,
            structuredOutput: value?.mappings ? (value?.mappings.length ? value?.mappings : undefined) : value,
            timestamp: response.timestamp || new Date().toISOString(),
            type: CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_AI,
          });
          setErrorMessage(null);
        }
        // eslint-disable-next-line no-underscore-dangle
      } else if (response.status === ChatbotStatus.ERROR || response.result?.__typename === 'AgentError') {
        addMessage({
          content: response.result?.message || 'Something went wrong. Please try again.',
          timestamp: response.timestamp || new Date().toISOString(),
          type: CopilotChatMessageType.ERROR,
          isError: true,
        });
      }
      if ([ChatbotStatus.READY, ChatbotStatus.ERROR].includes(response.status)) {
        setIsPollingEnabled(false);
      }
    },
    onError,
  });

  return {
    postMessage,
    isPollingEnabled: isPollingEnabled || isStartingSession || isPosting,
  };
};

// Old hooks
const INIT_CHATBOT_SESSION = gql`
  query initChatbotSession($request: InitChatbotSessionRequest!) {
    initChatbotSession(request: $request) {
      data {
        chatRunInfo
        defaultPrompts
        useFallbackQueries
      }
      error {
        message
        type
      }
    }
  }
`;

const PUBLISH_CHATBOT_MESSAGE = gql`
  mutation publishChatbotMessage($chatRunInfo: ChatRunInfoScalar, $userPrompt: String!) {
    publishChatbotMessage(chatRunInfo: $chatRunInfo, userPrompt: $userPrompt) {
      data
      error {
        message
        type
      }
    }
  }
`;

const GENERATE_CHATBOT_SUMMARY = gql`
  mutation generateChatbotSummary($chatRunInfo: ChatRunInfoScalar) {
    generateChatbotSummary(chatRunInfo: $chatRunInfo) {
      data
      error {
        message
        type
      }
    }
  }
`;

const GET_CHATBOT_MESSAGE = gql`
  query getChatbotMessage($chatRunInfo: ChatRunInfoScalar, $messageType: ChatbotMessageType!) {
    getChatbotMessage(chatRunInfo: $chatRunInfo, messageType: $messageType) {
      data {
        message
        status
      }
      error {
        message
        type
      }
    }
  }
`;

const END_CHATBOT_SESSION = gql`
  mutation endChatbotSession($chatRunInfo: ChatRunInfoScalar) {
    endChatbotSession(chatRunInfo: $chatRunInfo) {
      data
      error {
        message
        type
      }
    }
  }
`;

export const useInitChatbotSession = ({
  enabled = false,
  variables,
  onSuccess,
}: {
  enabled?: boolean;
  variables: InitChatbotSessionRequest;
  onSuccess?: (val: InitChatbotSessionResponse['initChatbotSession']) => void;
}) =>
  useGqlQuery<InitChatbotSessionResponse, InitChatbotSessionResponse['initChatbotSession']>({
    queryKey: [QueryKey.Copilot, QueryKey.InitChatbotSession, variables?.entityKey],
    query: INIT_CHATBOT_SESSION,
    variables: { request: variables },
    enabled: enabled && !!variables,
    gcTime: 0,
    staleTime: 0,
    onSuccess,
  });

export const usePublishChatbotMessage = (
  options?: Omit<UseMutationOptions<PublishChatbotMessageData, GraphQLError, PublishChatbotMessageRequest>, 'variablesAdapter'>
) =>
  useGqlMutation<PublishChatbotMessageData, PublishChatbotMessageRequest>({
    mutationKey: [QueryKey.Copilot, QueryKey.PublishChatbotMessage],
    mutation: PUBLISH_CHATBOT_MESSAGE,
    gcTime: 0,
    ...options,
  });

export const useGenerateChatbotSummary = (
  options?: Omit<UseMutationOptions<GenerateChatbotSummaryData, GraphQLError, GenerateChatbotSummaryRequest>, 'variablesAdapter'>
) =>
  useGqlMutation<GenerateChatbotSummaryData, GenerateChatbotSummaryRequest>({
    mutationKey: [QueryKey.Copilot, QueryKey.GenerateChatbotSummary],
    mutation: GENERATE_CHATBOT_SUMMARY,
    gcTime: 0,
    ...options,
  });

export const useEndOldChatbotSession = () =>
  useGqlMutation<EndChatbotSessionData, ChatbotEndSessionRequest>({
    mutationKey: [QueryKey.Copilot, QueryKey.EndChatbotSession],
    mutation: END_CHATBOT_SESSION,
  });

export const useGetChatbotMessage = ({
  chatRunInfo,
  messageType,
  enabled = false,
  onSuccess,
  onError,
}: {
  chatRunInfo: ChatRunInfo;
  messageType: CopilotGetStatusChatMessageType;
  enabled?: boolean;
  onSuccess?: (val: GetChatbotMessageResponse['getChatbotMessage']) => void;
  onError?: (error: GraphQLError) => void;
}) =>
  useGqlQuery<GetChatbotMessageResponse, GetChatbotMessageResponse['getChatbotMessage']>({
    queryKey: [QueryKey.Copilot, QueryKey.GetChatbotMessage, chatRunInfo.threadId],
    query: GET_CHATBOT_MESSAGE,
    variables: { chatRunInfo, messageType },
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    onSuccess,
    onError,
    refetchInterval: 3000,
  });

export const useChatbotConversation = ({
  isCopilotOpen = false,
  chatRunInfo,
  addMessage,
  setErrorMessage,
}: {
  isCopilotOpen: boolean;
  chatRunInfo: ChatRunInfo;
  addMessage: (message: ChatMessage) => void;
  setErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
}) => {
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [messageTypeState, setMessageTypeState] = useState(CopilotGetStatusChatMessageType.ANSWER);

  if (!isCopilotOpen && isPollingEnabled) {
    setIsPollingEnabled(false);
  }

  const onError = () => {
    setErrorMessage({
      message: 'Something went wrong. Please try again.',
      type: ChatbotErrorType.INTERNAL,
    });
    setIsPollingEnabled(false);
  };

  const { mutate: publishMessage, isPending } = usePublishChatbotMessage({
    onSuccess: () => {
      setIsPollingEnabled(true);
      setMessageTypeState(CopilotGetStatusChatMessageType.ANSWER);
    },
    onError,
  });

  const { mutate: summarizeMessage, isPending: isSummarizing } = useGenerateChatbotSummary({
    onSuccess: () => {
      setIsPollingEnabled(true);
      setMessageTypeState(CopilotGetStatusChatMessageType.SUMMARY);
    },
    onError,
  });

  useGetChatbotMessage({
    chatRunInfo,
    messageType: messageTypeState,
    enabled: isPollingEnabled && isCopilotOpen && !!chatRunInfo.threadId && !!messageTypeState,
    onSuccess: ({ data, error }) => {
      if (error) {
        setErrorMessage(error);
        setIsPollingEnabled(false);
      } else if (data?.status === ChatbotWfStatus.CHATBOT_READY && Object.keys(data.message).length) {
        addMessage(data.message);
        setErrorMessage(null);
        setIsPollingEnabled(false);
      }
    },
    onError,
  });

  return {
    publishMessage,
    summarizeMessage,
    isSummarizing: isSummarizing || (isPollingEnabled && messageTypeState === CopilotGetStatusChatMessageType.SUMMARY),
    isPollingEnabled: isSummarizing || isPending || isPollingEnabled,
  };
};
